import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MainContainer, MainHeaderContainer } from 'layouts/components';
import { ToastNotification, Loading } from 'carbon-components-react';
import DisplayEmails from './components/DisplayEmails';
import { EmailResponse } from './types';
import {
  fetchEmailsWithCriteria,
  ignoreEmails,
  resendEmails,
  resendToOtherEmails
} from 'services/emails.service';
import Utils from 'utilities/helper.util';
import { filterStr, filterStringReducer } from './components/utils';
import { selectUser } from 'redux/user/selector';
import { updateProfileUpdatingFlag } from 'redux/user/actions';
import { SendToOtherModal } from 'components';

export const initialValues = {
  dateFrom: Utils.getNewDate().subtract(14, 'days').format('DD/MM/YYYY'),
  dateTo: Utils.getTodayDate(),
  hourFrom: '12:00',
  amPmFrom: 'AM',
  hourTo: '11:59',
  amPmTo: 'PM',
  status: ['Bounced', 'Dropped'],
  userId: 0,
  isIgnored: 'false',
  isResent: 'false'
};

const setupInitialFilter = (userId: any) => {
  const initValues = {
    ...initialValues,
    userId: userId
  };
  const formData = Object.entries({
    ...initValues
  }).reduce(filterStringReducer, {});
  const filteredString = filterStr({ formData });
  return filteredString;
};

const EmailsUndelivered: React.FC<any> = () => {
  const dispatch = useDispatch();
  const user: any = useSelector(selectUser);

  const userEmail = user?.alternateEmail || user?.email;
  const [totalSelected, setTotalSelected] = useState<any>(0);
  const [pageData, setPageData] = useState<any>({
    emails: null,
    pageInfo: {
      count: 0,
      currentPage: 0,
      totalPages: 0
    }
  });
  const [showSendToOtherModal, setShowSendToOtherModal] = useState<boolean>(
    false
  );
  const [sendToOtherId, setSendToOtherId] = useState([]);

  const handleOpenProfile = () => {
    dispatch(updateProfileUpdatingFlag(true));
  };

  const [successAction, setSuccessAction] = useState<any>(null);
  const initialPageProps = {
    recordsPerPage: 10,
    offset: 0
  };
  const [pageReloadTriggers, setpageReloadTriggers] = useState<any>({
    filter: setupInitialFilter(user?.id),
    direction: 'outbound',
    refresh: false,
    pageInfo: initialPageProps
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pageWrapper = useRef(null);

  const handleFetchNextPage = async (props: any) => {
    setpageReloadTriggers({
      ...pageReloadTriggers,
      pageInfo: {
        ...pageReloadTriggers.pageInfo,
        recordsPerPage: props.pageSize,
        offset: props.page * props.pageSize - props.pageSize
      }
    });
  };

  const handleRefreshPage = async () => {
    setpageReloadTriggers({
      filter: setupInitialFilter(user?.id),
      direction: 'outbound',
      refresh: false,
      pageInfo: initialPageProps
    });
  };

  const ignoreEmail = async (emailIdArray: any, batchAct: any) => {
    batchAct.shouldShowBatchActions = false;
    setSuccessAction(null);
    const ignoreActions = [];
    for (const id of emailIdArray) {
      ignoreActions.push(ignoreEmails({ emailId: id }));
    }
    try {
      setIsLoading(true);
      await Promise.all(ignoreActions);
    } catch (err) {
      setApiError('Error fetching ignore emails');
    } finally {
      handleRefreshPage();
      setSuccessAction({
        count: ignoreActions.length,
        action:
          ignoreActions.length > 1 ? 'items were ignored' : 'item were ignored',
        time: Utils.getNewDate().format('hh:mm:ss A')
      });
    }
  };

  const resendEmail = async (emailIdArray: any, emailAddress: string) => {
    setSuccessAction(null);
    const resendActions = [];
    for (const id of emailIdArray) {
      if (emailAddress) {
        resendActions.push(resendToOtherEmails({ emailId: id, emailAddress }));
      } else {
        resendActions.push(resendEmails({ emailId: id }));
      }
    }
    try {
      setIsLoading(true);
      await Promise.all(resendActions);
    } catch (err) {
      setApiError('Error fetching resend emails');
    } finally {
      handleRefreshPage();
      setSuccessAction({
        count: resendActions.length,
        action: resendActions.length > 1 ? 'items were sent' : 'item were sent',
        time: Utils.getNewDate().format('hh:mm:ss A')
      });
    }
  };

  const onSendToOtherClick = (row: any) => {
    setSendToOtherId([]);
    const emailId = row.map((r: any) => {
      return parseInt(r.id);
    });
    setShowSendToOtherModal(true);
    setSendToOtherId(emailId);
  };

  const onClickSend = (val: any) => {
    const emailAddress = val;
    const emailId = sendToOtherId;
    resendEmail(emailId, emailAddress);
    setShowSendToOtherModal(false);
  };

  useEffect(() => {
    const fetchAPI = async () => {
      setIsLoading(true);
      const params = {
        fields: 'subject,id,createdAt,status,userId',
        order: '-createdAt',
        include: 'Attachment',
        limit: pageReloadTriggers.pageInfo.recordsPerPage,
        offset: pageReloadTriggers.pageInfo.offset
      };
      try {
        const response: any = await fetchEmailsWithCriteria({
          ...params,
          filter: pageReloadTriggers.filter,
          direction: pageReloadTriggers.direction
        });
        const responseType = 'OutboundEmails';
        const emails: Array<EmailResponse> = response[responseType].map(
          (email: EmailResponse) => {
            return {
              from: email.from,
              to: email.to,
              subject: email.subject,
              id: email.id,
              sent: email.createdAt,
              attachment: email.Attachments.length,
              status: email.status,
              pageInfo: email.Info
            };
          }
        );
        setPageData((p: any) => ({
          ...p.pageData,
          emails,
          pageInfo: {
            count: response.Info.count,
            currentPage:
              response.Info.offset /
                pageReloadTriggers.pageInfo.recordsPerPage +
              1,
            totalPages:
              response.Info.count / pageReloadTriggers.pageInfo.recordsPerPage
          }
        }));
      } catch (err) {
        setApiError('Error fetching emails details');
        setPageData((p: any) => ({ ...p.pageData, emails: null }));
      } finally {
        setIsLoading(false);
      }
    };
    fetchAPI();
  }, [pageReloadTriggers]);

  const pageInfo = {
    currentPage: pageData.pageInfo.currentPage,
    pageSize: pageReloadTriggers.pageInfo.recordsPerPage,
    totalItems: pageData.pageInfo.count,
    handleFetchNextPage
  };

  return (
    <div
      className="undelivered-emails-main-container container--wrapper"
      ref={pageWrapper}
    >
      <MainHeaderContainer enableHeaderGlobalBar />
      <MainContainer>
        <div className="undelivered-emails-body-wrapper">
          {successAction && (
            <div className="undelivered-emails-success-notification">
              <ToastNotification
                kind={'success'}
                iconDescription="Success"
                title={`${successAction.count} ${successAction.action}`}
                caption={successAction.time}
                timeout={5000}
              />
            </div>
          )}
          <section className="undelivered-emails-header--wrapper">
            {apiError && (
              <ToastNotification
                lowContrast={false}
                caption=""
                iconDescription="Error fetching api"
                title={apiError}
                onCloseButtonClick={() => setApiError(null)}
              />
            )}
            <div className="undelivered-emails-header">
              <h1 className="main-heading">Undelivered emails</h1>
              <p className="main-description">Select an item to manage</p>
            </div>
          </section>
          <section className="undelivered-emails-description--wrapper">
            <div className="undelivered-emails-divider"></div>
            <div className="undelivered-emails-description">
              Response emails were sent to <span>{userEmail}</span>. Your
              send-to email address can be changed in{' '}
              <span onClick={handleOpenProfile}>User profile</span>.
            </div>
          </section>
          {/* <section className="undelivered-emails-content--wrapper"> */}
          <section
            className={
              totalSelected > 0
                ? 'undelivered-emails-content--wrapper'
                : 'decrease-top undelivered-emails-content--wrapper'
            }
          >
            {pageData?.emails !== null && (
              <DisplayEmails
                emails={pageData?.emails}
                pageInfo={pageInfo}
                direction={pageReloadTriggers.direction}
                setApiError={setApiError}
                ignoreEmail={ignoreEmail}
                resendEmail={resendEmail}
                setTotalSelected={setTotalSelected}
                totalSelected={totalSelected}
                handleSendToOtherClick={onSendToOtherClick}
              />
            )}
          </section>
        </div>
        <SendToOtherModal
          isModalOpen={showSendToOtherModal}
          onModalClose={() => setShowSendToOtherModal(false)}
          onClickSend={onClickSend}
        />
      </MainContainer>
      {isLoading && <Loading withOverlay={true} />}
    </div>
  );
};

EmailsUndelivered.displayName = 'EmailsUndelivered';
export default EmailsUndelivered;
